import { useMemo } from 'react';

import { PSA_CONSTANTS } from '@nile/components/src/Constants/PSA';

import { includes } from 'lodash';

const { GROUP_BY_CATEGORY } = PSA_CONSTANTS;

export function groupItemsByType(items = []) {
  return items.reduce(
    (groups: Record<keyof typeof GROUP_BY_CATEGORY, unknown[]>, item) => {
      if (item?.offeredItem?.type === 'CONTRACT') {
        groups.CONTRACT.push(item);
        return groups;
      }

      if (item?.offeredItem?.type === 'WORK') {
        const moduleType = item?.offeredItem?.workItem?.module;
        if (moduleType === 'TICKET' || moduleType === 'PROJECT') {
          groups.WORKLOG.push(item);
          return groups;
        }
        // // Late fee should fall under SERVICE
        // if (moduleType === 'INVOICE') {
        //   groups.SERVICE.push(item);
        //   return groups;
        // }
      }

      const itemType = item?.serviceCatalogItem?.itemType;
      if (itemType === 'SERVICE') {
        groups.SERVICE.push(item);
        return groups;
      }
      if (itemType === 'PRODUCT') {
        groups.PRODUCT.push(item);
        return groups;
      }

      return groups;
    },
    {
      CONTRACT: [],
      WORKLOG: [],
      SERVICE: [],
      PRODUCT: []
    }
  );
}

export function useGroupByCategory({ data, invoiceSettings }) {
  const isGroupByCategoryEnabled = useMemo(() => {
    if (invoiceSettings?.invoiceTemplate?.preferences) {
      return (
        invoiceSettings.invoiceTemplate.preferences.groupBy ===
        PSA_CONSTANTS.GROUP_BY.SERVICE_CATEGORY
      );
    }

    return false;
  }, [invoiceSettings]);

  const dataGroupedByCategory: Array<{
    label: string;
    items: unknown[];
  }> = useMemo(() => {
    if (!isGroupByCategoryEnabled) {
      return [];
    }
    const grouped = groupItemsByType(data?.items || []);

    const orderFromSettings =
      invoiceSettings?.invoiceTemplate?.preferences?.groupByOrder;

    const fallbackOrder = [
      { label: 'Product', value: 'PRODUCT' },
      { label: 'Service', value: 'SERVICE' },
      { label: 'Worklog', value: 'WORKLOG' },
      { label: 'Contract', value: 'CONTRACT' }
    ] as Array<{ label: string; value: keyof typeof GROUP_BY_CATEGORY }>;

    const effectiveOrder = orderFromSettings?.length
      ? orderFromSettings
      : fallbackOrder;

    return effectiveOrder.reduce(
      (acc: Array<{ label: string; items: unknown[] }>, entry) => {
        const value = entry?.value as keyof typeof GROUP_BY_CATEGORY;
        const label = entry?.label ?? entry?.value;
        const items = grouped?.[value] || [];

        if (items.length > 0) {
          let totalHours = 0;

          if (includes([GROUP_BY_CATEGORY.WORKLOG], value)) {
            totalHours = items
              .map((item) => {
                if (
                  item?.serviceCatalogItem?.serviceTypeItem?.offeringType ===
                  'HOURS'
                ) {
                  return Number(item.quantity ?? '');
                }
                return 0;
              })
              .reduce((s, n) => s + n, 0);
          }

          acc.push({
            label,
            items,
            type: value,
            hasTotalHours: totalHours > 0,
            totalHours: `${totalHours} h`
          });
        }
        return acc;
      },
      []
    );
  }, [data, invoiceSettings, isGroupByCategoryEnabled]);

  return { isGroupByCategoryEnabled, dataGroupedByCategory };
}
